/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { Button, Grid, makeStyles } from '@material-ui/core';
import _isEmpty from 'lodash/isEmpty';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch } from 'react-redux';
import { Typography } from '@mui/material';
import Theme from '../ui/Theme';
import Service from '../../apis/service';
import { showProgress } from '../../redux/actions/DealershipActions';
import { ErrorToast, notifySuccess, searchArray, formatDate } from '../utils/Utils';
import DealershipList from './DealershipList';
import CollectionList from './CollectionList';
import DealershipHeader from './DealershipHeader';
import DateStep from './DateStep';
import Steps from '../common/Steps';
import { apiEndpoints } from '../../constants/apiEndpoints';
import { PxFilters } from '../common/PxFilters';

const customStyles = {
  content: {
    height: '90vh',
    width: '90vw',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '15px',
    display: 'block',
  },
  overlay: {
    zIndex: 1000,
  },
};

const useStyles = makeStyles(() => ({
  popupHeading: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '22px',
    fontStyle: 'normal',
    padding: '15px',
  },
  popupCloseBtn: {
    borderRadius: '50%',
    backgroundColor: Theme.palette.secondary.main,
    padding: '8px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    cursor: 'pointer',
  },
  mainDiv: {
    marginTop: '10px',
    marginBottom: '10px',
    backgroundColor: Theme.palette.secondary.main,
    // backgroundColor: "white",
    maxHeight: '70%',
    height: '70%',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': { width: '10px' },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 5px grey',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'lightGrey',
      borderRadius: '100px',
    },
  },
  dealersDiv: {
    maxHeight: '8%',
    height: '8%',
  },
  popSubmitBtn: {
    display: 'flex',
    padding: '0 70px',
    height: '45px',
    width: '62px',
    color: 'white',
    borderRadius: '4px',
    marginTop: '6px',
  },
  popCancelBtn: {
    display: 'flex',
    padding: '0 70px',
    height: '45px',
    width: '62px',
    marginRight: '28px',
    borderRadius: '4px',
    marginTop: '6px',
  },
  bottomDiv: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'stretch',
  },
  dealershipsContainer: {
    display: 'flex',
    padding: '12px',
    alignItems: 'flex-start',
    alignSelf: 'strech',
    gap: '16px',
    borderRadius: '8px',
    border: '1px solid var(--brand-200, #D9D6FE)',
    background: 'var(--brand-50, #FFFFFF)',
    maxHeight: '30%',
    height: '30%',
  },
  filterContainer: {
    display: 'flex',
    padding: '12px',
    alignItems: 'flex-start',
    alignSelf: 'strech',
    gap: '16px',
    borderRadius: '8px',
    border: '1px solid var(--brand-200, #D9D6FE)',
    background: 'lightgrey',
    maxHeight: '30%',
    height: '47%',
    marginTop: '10px',
  },
  selectedInfo: {
    display: 'flex',
    padding: '10px 20px',
    background: '#f4f4f4',
    gap: '30px',
  },
}));

export default function AddNewTopicPopup({ closePopup }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [topicSelectedId, setTopicSelectedId] = useState(null);
  const [filteredCollectionList, setFilteredCollectionList] = useState([]);
  const [searchCollectionValue, setSearchCollectionValue] = useState('');
  const [dealershipListForTopic, setDealershipListForTopic] = useState([]);
  const [selectedStartDate, handleStartDateChange] = useState(new Date(), {
    format: 'YYYY-MM-DD',
  });
  const [selectedEndDate, handleEndDateChange] = useState(new Date(), {
    format: 'YYYY-MM-DD',
  });
  const [selectedList, setSelectedList] = useState({});
  const [collectionList, setCollectionList] = useState([]);
  const [collectionTabs, setCollectionTabs] = useState([]);
  const [topicSelected, setTopicSelected] = useState({});

  const [appliedFilters, setAppliedFilters] = useState({});
  const [filters, setFilters] = useState({});
  const DEFAULT_FILTERS = useMemo(() => {
    const sales = filters?.salesPipelineStatus?.find((i) => i.title === 'Active') || null;
    return {
      salesPipelineStatus: sales ? [sales] : [],
      source: ['ADMIN'],
      dealerships: [],
      dealerGroup: [],
      pxOwners: filters?.pxOwners?.map((item) => item) || [],
      plans: [filters?.plans?.find((item) => item.id === 2)],
    };
  }, [filters]);

  useEffect(() => {
    setAppliedFilters(DEFAULT_FILTERS);
  }, [DEFAULT_FILTERS]);

  const getFilters = async () => {
    dispatch(showProgress(true));
    try {
      const res = await Service.get(apiEndpoints.DEALER_FILTER);
      setFilters(res || {});
    } catch (error) {
      ErrorToast(error);
    }

    dispatch(showProgress(false));
  };

  useEffect(() => {
    getFilters();
  }, []);

  const handleDateChange = (name, date) => {
    if (name === 'startDate') {
      handleStartDateChange(date);
    } else {
      handleEndDateChange(date);
    }
  };

  useEffect(() => {
    setFilteredCollectionList(collectionList || []);
    setSearchCollectionValue('');
  }, [collectionList]);

  const fetchData = async () => {
    dispatch(showProgress(true));
    try {
      const response = await Service.get(apiEndpoints.TOM_TOPICS);
      const temp = response.reduce((acc, item) => {
        acc[item.title] = item.topics;
        return acc;
      }, {});

      setCollectionList(temp['All Departments']);
      setCollectionTabs(temp);
    } catch (err) {
      ErrorToast(err);
    }
    dispatch(showProgress(false));
  };

  const fetchDealershipListForTopic = async () => {
    dispatch(showProgress(true));
    const sales = appliedFilters.salesPipelineStatus?.map((i) => i.id) || [];
    const groupIds = appliedFilters.dealerGroup?.map((i) => i.id) || [];
    const PxHash = appliedFilters.pxOwners?.map((i) => i.hash) || [];
    const planIds = appliedFilters.plans?.map((i) => i.id) || [];

    try {
      const response = await Service.post(apiEndpoints.TOM_TOPIC_DEALERS.replace('ID', topicSelectedId), {
        startDate: formatDate(selectedStartDate),
        endDate: formatDate(selectedEndDate),
        filters: {
          source: appliedFilters.source,
          dealerGroupIds: groupIds,
          salesPipelineStatus: sales,
          pxOwnerHash: PxHash,
          plans: planIds,
        },
      });
      setDealershipListForTopic(response);
    } catch (err) {
      ErrorToast(err);
    }
    dispatch(showProgress(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (step === 3) {
      fetchDealershipListForTopic();
    }
  }, [step]);

  const handleNextStep = () => {
    // TODO: DO data validation and move to next step
    if (step === 1) {
      if (!topicSelectedId) {
        ErrorToast('Select a topic.');
        return;
      }
    } else if (step === 2) {
      if (!selectedStartDate || !selectedEndDate) {
        ErrorToast('Plese select start date and end date');
        return;
      }

      const startDate = new Date(selectedStartDate);
      const endDate = new Date(selectedEndDate);
      if (endDate < startDate) {
        ErrorToast('End date must be in the future of the start date.');
        return;
      }
    } else if (step === 3) {
      if (_isEmpty(selectedList)) {
        ErrorToast('Please select atleast one dealership.');
        return;
      }
    }
    setStep((t) => t + 1);
  };

  const handleStepBack = () => {
    setStep((t) => t - 1);
  };

  useEffect(() => {
    setSelectedList({});
    setDealershipListForTopic([]);
  }, [selectedEndDate, selectedStartDate, topicSelectedId]);

  const handleApplyAll = (_, currentDealershipId) => {
    if (!currentDealershipId) {
      ErrorToast('Please select a dealership first');
      return;
    }

    const currentDealershipSelection = selectedList[currentDealershipId];

    if (!currentDealershipSelection || Object.keys(currentDealershipSelection).length === 0) {
      ErrorToast('No departments/roles selected in the active dealership.');
      return;
    }

    const updatedList = {};

    dealershipListForTopic.forEach((dealer) => {
      if (dealer.id in selectedList) {
        const validDepartments = dealer.departments?.map((dept) => String(dept.id)) || [];

        const newDeptMap = {};

        Object.entries(currentDealershipSelection).forEach(([deptId, roles]) => {
          if (validDepartments.includes(String(deptId)) && roles.length > 0) {
            newDeptMap[deptId] = [...roles];
          }
        });

        updatedList[dealer.id] = newDeptMap;
      }
    });

    setSelectedList(updatedList);
    notifySuccess(`Department selections applied to all selected dealerships`);
  };

  const handleSubmit = async () => {
    if (_isEmpty(selectedList)) {
      ErrorToast('Please select atleast one dealership.');
      return;
    }
    dispatch(showProgress(true));
    try {
      await Service.post(apiEndpoints.TOM_ASSIGN, {
        topicId: topicSelectedId,
        dealerships: selectedList,
        startDate: formatDate(selectedStartDate),
        endDate: formatDate(selectedEndDate),
      });
      notifySuccess('Topic Assigned Successfully');
      closePopup(formatDate(selectedStartDate), true);
    } catch (err) {
      ErrorToast(err);
    }

    dispatch(showProgress(false));
  };

  const handleCollectionClicked = (topic) => {
    setTopicSelectedId(topic.id);
    setTopicSelected(topic);
  };
  const handleSearchCollection = (e) => {
    const input = e.target.value;
    setSearchCollectionValue(input);
    const filteredIArray = searchArray(collectionList || [], ['title'], input);
    setFilteredCollectionList(filteredIArray);
  };

  // eslint-disable-next-line no-unused-vars
  const filterContainer = (
    <div className={classes.dealersDiv}>
      <div className={classes.dealershipsContainer}>
        <Grid container></Grid>
      </div>
    </div>
  );

  const mainContainer = () => {
    if (step === 1) {
      return (
        <CollectionList
          handleSearchCollection={handleSearchCollection}
          searchCollectionValue={searchCollectionValue}
          filteredCollectionList={filteredCollectionList}
          handleCollectionClicked={handleCollectionClicked}
          topicSelectedId={topicSelectedId}
          setCollectionList={setCollectionList}
          collectionTabs={collectionTabs}
        />
      );
    }
    if (step === 2) {
      return (
        <DateStep
          selectedStartDate={selectedStartDate}
          handleDateChange={handleDateChange}
          selectedEndDate={selectedEndDate}
          topicSelected={topicSelected}
        />
      );
    }
    if (step === 3) {
      return (
        <>
          {/* {filterContainer} */}
          <DealershipHeader />
          <DealershipList
            dealershipListForTopic={dealershipListForTopic}
            selectedList={selectedList}
            setSelectedList={setSelectedList}
            topicSelected={topicSelected}
            onApplyAllDepartment={handleApplyAll}
          />
        </>
      );
    }
    return <div className={classes.mainDiv}>Something went wrong</div>;
  };

  return (
    <Modal isOpen onRequestClose={() => closePopup(null, false)} style={customStyles}>
      <Grid container direction="row" alignItems="center">
        <Grid item xs={11} className={classes.popupHeading}>
          Assign Topic
        </Grid>
        <Grid item xs={1}>
          <CloseIcon className={classes.popupCloseBtn} onClick={() => closePopup(null, false)} />
        </Grid>
      </Grid>
      <hr />
      <Steps step={step} stepInfo={['Select Topic', 'Select Date', 'Assign To']} />
      <hr />
      <Grid className={classes.selectedInfo}>
        {step > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography>Topic:</Typography>
            <Typography>
              <span style={{ fontWeight: '700' }}>{topicSelected?.title}</span>
              <span>(Id: {topicSelected?.id})</span>
            </Typography>
          </div>
        )}
        {step > 2 && (
          <div>
            <Typography>Date:</Typography>
            <Typography>
              <span style={{ fontWeight: '700' }}>{formatDate(selectedStartDate)}</span>
              <span style={{ margin: '0px 5px' }}>to</span>
              <span style={{ fontWeight: '700' }}>{formatDate(selectedEndDate)}</span>
            </Typography>
          </div>
        )}
      </Grid>
      {step === 3 && (
        <Grid style={{ background: '#F4F4F4', marginBottom: '10px' }}>
          <PxFilters
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            DEFAULT_FILTERS={DEFAULT_FILTERS}
            filters={filters}
            onFilter={fetchDealershipListForTopic}
            showApplyAll={Object.keys(selectedList).length > 1}
            onApplyAll={() => {
              if (window.applyDepartmentToAllDealerships) {
                window.applyDepartmentToAllDealerships();
              } else {
                ErrorToast('Please select a department in the dealership list first');
              }
            }}
          />
        </Grid>
      )}
      {mainContainer()}
      <hr />
      <div className={classes.bottomDiv}>
        {step === 1 ? (
          <Button variant="outlined" className={classes.popCancelBtn} onClick={() => closePopup(null, false)}>
            {' '}
            Close
          </Button>
        ) : (
          <Button variant="outlined" className={classes.popCancelBtn} onClick={handleStepBack}>
            {' '}
            Back
          </Button>
        )}
        {step < 3 ? (
          <Button variant="contained" color="primary" className={classes.popSubmitBtn} onClick={handleNextStep}>
            {' '}
            Next
          </Button>
        ) : (
          <Button variant="contained" color="primary" className={classes.popSubmitBtn} onClick={handleSubmit}>
            {' '}
            Assign
          </Button>
        )}
      </div>
    </Modal>
  );
}
